import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { BellRing, Play, RotateCcw, Send, Sparkles, Clock, Bot, User, CheckCircle2, AlertTriangle, Database } from 'lucide-react';
import { t } from '@/i18n';
import { getCarrier } from '@/data/arrivalsData';
import { channelIcons, channelLabel } from './arrivalsUi';
import { HelpTip } from '@/components/ui/help-tip';

// Channels are mocked in the demo; badges say so.
const mockedChannels = ['sms', 'email', 'whatsapp', 'voice'];

const kindStyles = {
  out: 'bg-primary/5 border-primary/20',
  in: 'bg-emerald-50 border-emerald-200',
  call: 'bg-violet-50 border-violet-200',
  gps: 'bg-sky-50 border-sky-200',
  sap: 'bg-amber-50 border-amber-200',
  escalation: 'bg-orange-50 border-orange-200',
  system: 'bg-muted border-border',
  done: 'bg-emerald-50 border-emerald-300',
};

const LogEntry = ({ entry, loadingsById, onSelect }) => {
  const [open, setOpen] = useState(false);
  const loading = entry.loadingId ? loadingsById[entry.loadingId] : null;
  const Icon = entry.kind === 'sap' ? Database
    : entry.kind === 'done' ? CheckCircle2
      : entry.kind === 'escalation' ? AlertTriangle
        : channelIcons[entry.channel || entry.kind] || Clock;

  return (
    <div className={cn('rounded-lg border p-2.5 text-xs animate-fade-in-up', kindStyles[entry.kind], entry.failed && 'bg-red-50 border-red-200')}>
      <div className="flex items-start gap-2">
        <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold tabular-nums">{entry.time}</span>
            {entry.kind === 'out' && <span className="text-muted-foreground">→ {channelLabel(entry.channel)}</span>}
            {entry.kind === 'in' && <span className="text-muted-foreground">← {channelLabel(entry.channel)}</span>}
            {loading && (
              <button className="font-semibold text-primary hover:underline" onClick={() => onSelect(loading)}>
                {loading.plate}
              </button>
            )}
            {loading && <span className="text-muted-foreground truncate">{getCarrier(loading.carrierId).name}</span>}
          </div>
          <div className={cn(entry.kind === 'out' && 'text-muted-foreground line-clamp-2')}>{entry.text}</div>
          {entry.textCs && entry.kind !== 'out' && <div className="text-muted-foreground italic">{entry.textCs}</div>}
          {entry.interpretation && (
            <div className="flex items-center gap-1 font-medium text-emerald-700">
              <Sparkles className="h-3 w-3" /> {entry.interpretation}
            </div>
          )}
          {entry.transcript && (
            <>
              <button className="text-primary hover:underline" onClick={() => setOpen(o => !o)}>
                {open ? t('arrivals.hideTranscript') : t('arrivals.showTranscript')}
              </button>
              {open && (
                <div className="space-y-1 pt-1">
                  {entry.transcript.map((line, i) => (
                    <div key={i} className="flex gap-1.5">
                      {line.who === 'bot' ? <Bot className="h-3 w-3 mt-0.5 text-violet-600 shrink-0" /> : <User className="h-3 w-3 mt-0.5 shrink-0" />}
                      <span>
                        {line.text}
                        {line.textCs && <span className="block text-muted-foreground italic">{line.textCs}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const UrgencyPanel = ({ sim, onSelect }) => {
  const { loadings, log, phase, clock, queue, autoMode, setAutoMode, runCheck, approve, reset } = sim;
  const loadingsById = Object.fromEntries(loadings.map(l => [l.id, l]));
  const unknownLeft = loadings.filter(l => l.state === 'no-info').length;

  return (
    <Card className="p-4 space-y-4" data-tour="urgency-panel">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
            <BellRing className="h-4 w-4 text-red-600" />
          </div>
          <div>
            <div className="font-semibold flex items-center gap-1">{t('arrivals.urgency.title')} <HelpTip id="followUp" /></div>
            <div className="text-xs text-muted-foreground">{t('arrivals.urgency.rule')}</div>
          </div>
        </div>
        <Badge variant="outline" className="gap-1 tabular-nums shrink-0">
          <Clock className="h-3 w-3" /> {clock}
        </Badge>
      </div>

      <div className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
        <div className="text-xs">
          <div className="font-medium flex items-center gap-1">{autoMode ? t('arrivals.urgency.auto') : t('arrivals.urgency.approval')} <HelpTip id="approval" /></div>
          <div className="text-muted-foreground">{autoMode ? t('arrivals.urgency.autoHint') : t('arrivals.urgency.approvalHint')}</div>
        </div>
        <Switch checked={autoMode} onCheckedChange={setAutoMode} disabled={phase !== 'idle'} />
      </div>

      {phase === 'idle' && (
        <Button className="w-full gap-2" onClick={runCheck} data-tour="urgency-run">
          <Play className="h-4 w-4" /> {t('arrivals.urgency.run')}
        </Button>
      )}

      {phase === 'review' && (
        <div className="space-y-2">
          <div className="text-xs font-semibold">{t('arrivals.urgency.queue')} ({queue.length})</div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {queue.map(id => {
              const l = loadingsById[id];
              const carrier = getCarrier(l.carrierId);
              const ch = carrier.channel === 'phone' ? 'sms' : carrier.channel;
              const Icon = channelIcons[ch];
              return (
                <div key={id} className="flex items-center gap-2 text-xs rounded-md border px-2 py-1.5">
                  <Icon className="h-3 w-3 text-muted-foreground" />
                  <button className="font-semibold hover:underline" onClick={() => onSelect(l)}>{l.plate}</button>
                  <span className="text-muted-foreground truncate flex-1">{carrier.name}</span>
                  <span className="text-muted-foreground">{carrier.lang.toUpperCase()}</span>
                </div>
              );
            })}
          </div>
          <Button className="w-full gap-2" onClick={approve} data-tour="urgency-approve">
            <Send className="h-4 w-4" /> {t('arrivals.urgency.sendAll')} ({queue.length})
          </Button>
        </div>
      )}

      {phase === 'done' && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs space-y-1">
          <div className="font-semibold text-emerald-800">
            {t('arrivals.urgency.result')}: {queue.length + 1} → {unknownLeft}
          </div>
          <div className="text-emerald-800/80">{t('arrivals.urgency.resultHint')}</div>
        </div>
      )}

      {phase !== 'idle' && (
        <Button variant="outline" size="sm" className="w-full gap-2 text-xs" onClick={reset}>
          <RotateCcw className="h-3 w-3" /> {t('arrivals.urgency.reset')}
        </Button>
      )}

      {/* Log */}
      {log.length > 0 ? (
        <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1" data-tour="urgency-log">
          {log.map(entry => (
            <LogEntry key={entry.id} entry={entry} loadingsById={loadingsById} onSelect={onSelect} />
          ))}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground text-center py-4">{t('arrivals.urgency.empty')}</div>
      )}

      <div className="flex flex-wrap gap-1.5 pt-2 border-t">
        {mockedChannels.map(channel => (
          <Badge key={channel} variant="outline" className="text-[10px] gap-1 font-normal">
            <span className="text-amber-500" aria-hidden="true">●</span> {channelLabel(channel)} · {t('arrivals.readyToConnect')}
          </Badge>
        ))}
      </div>
    </Card>
  );
};
